import React from "react";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/common/theme-provider";
import TabsPage from "./pages/TabsPage";
import NotesPage from "./pages/NotesPage";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/LoginPage";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/route/ProtectedRoute";
import { PublicRoute } from "./components/route/PublicRoute";
import IndexPage from "./pages/IndexPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";

const queryClient = new QueryClient();

const isChromeExtension = typeof chrome !== "undefined" && !!chrome.runtime && !!chrome.runtime.id;

const App = () => {
  // State to hold the dynamically loaded Analytics component
  const [Analytics, setAnalytics] = React.useState<React.ComponentType | null>(null);

  React.useEffect(() => {
    if (!isChromeExtension) {
      // Dynamically import Analytics only if not a Chrome extension
      import('@vercel/analytics/react').then(mod => {
        setAnalytics(() => mod.Analytics);
      });
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="hintword-theme">
        <TooltipProvider>
          <AuthProvider>
            <div className="min-h-screen bg-background text-foreground">
              <Toaster />
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                {isChromeExtension ?
                  <Route path="/" element={<Navigate to="/tabs" replace />} /> :
                  <Route path="/" element={<PublicRoute><IndexPage /></PublicRoute>} />
                }
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route
                  path="/tabs"
                  element={
                    <ProtectedRoute>
                      <TabsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notes"
                  element={
                    <ProtectedRoute>
                      <NotesPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
              {/* Only render Analytics if not a Chrome extension and after dynamic import */}
              {Analytics && <Analytics />}
            </div>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
